/**
 * Chip catalogue for the per-pipeline activity log's SERVER-SIDE filters
 * (SE-6251).
 *
 * `pipelineActivityLog` takes `action` and `actorType` as LISTS, so a chip row
 * is multi-select and a chip may stand for more than one enum value. Every chip
 * here is therefore just a named bundle of enum members that gets unioned into
 * the request — the grouping is presentation, the filtering is entirely the
 * backend's. Nothing in this module can drop a row that the server returned.
 *
 * Two invariants the tests pin:
 *
 * - **Every `PipelineActivityAction` appears in exactly one action chip.** A
 *   member missing from the catalogue would be unreachable through the UI while
 *   still arriving in the unfiltered feed, which reads as "the filter is
 *   broken". A member in two chips would double up in the request.
 * - **The `AUTOMATED` chip is exactly the set `isAutomatedActor` treats as
 *   automated** (`AUTOMATION_RULE` + `SYSTEM`). That equality is what makes
 *   "filter to automated entries → only entries carrying the automation marker
 *   come back" true rather than approximately true.
 *
 * Lives in `utils/` (not beside the View) because component directories are
 * restricted to index/Container/View files by the component-structure lint
 * rule — the same reason `pipelineActivityRows.ts` lives here.
 * @module features/player-kanban/utils/pipelineActivityFilters
 */

import { PipelineActivityAction, PipelineActorType } from "@/generated/graphql";

/**
 * The part of a chip a View needs: identity and label. Views never see the enum
 * payload, which is what keeps them structurally incapable of filtering.
 */
export interface PipelineActivityChipLabel {
  /** Stable chip key — used for test identity and the handler map. */
  readonly key: string;
  /** Visible label. */
  readonly label: string;
}

/** One multi-select filter chip: a label over a bundle of enum values. */
export interface PipelineActivityFilterChip<
  TValue,
> extends PipelineActivityChipLabel {
  /** Enum members this chip contributes to the request when selected. */
  readonly values: readonly TValue[];
}

/**
 * The "clear everything" chip (SE-6554).
 *
 * Deliberately NOT a member of either catalogue: it stands for no enum values
 * at all, and the invariant tests below walk the catalogues asserting every
 * chip contributes exactly one disjoint bundle. Its selected state is likewise
 * derived rather than stored — it is "on" precisely when both selections are
 * empty, which is already the state that omits both filter arguments from the
 * request. There is therefore no way for the chip to disagree with the query.
 */
export const ALL_FILTER_CHIP: PipelineActivityChipLabel = {
  key: "all",
  label: "All",
};

/**
 * Action chips.
 *
 * The bundles group members that a manager reads as one kind of event (both
 * tag events, all three review decisions, report and notes writes). Ordering
 * runs movement → membership → review → tags → reports → lifecycle, which is
 * roughly how often each appears in a live pipeline's feed.
 */
export const ACTION_FILTER_CHIPS: readonly PipelineActivityFilterChip<PipelineActivityAction>[] =
  [
    {
      key: "moves",
      label: "Moves",
      values: [PipelineActivityAction.Moved],
    },
    {
      key: "membership",
      label: "Added / removed",
      values: [PipelineActivityAction.Added, PipelineActivityAction.Removed],
    },
    {
      key: "reviews",
      label: "Reviews",
      values: [
        PipelineActivityAction.ReviewRequested,
        PipelineActivityAction.ReviewAccepted,
        PipelineActivityAction.ReviewDeclined,
      ],
    },
    {
      key: "tags",
      label: "Tags",
      values: [
        PipelineActivityAction.TagApplied,
        PipelineActivityAction.TagRemoved,
      ],
    },
    {
      // SE-6925 folds `NOTES_UPDATED` in here rather than opening a seventh
      // chip: a scout report and a card's notes are the same thing to the
      // manager reading the feed — written assessment attached to the player —
      // and every chip added is a chip the row has to fit. Widening an existing
      // bundle is also how `TAG_REMOVED` entered the catalogue (SE-6864).
      key: "reports",
      label: "Reports / notes",
      values: [
        PipelineActivityAction.ReportAdded,
        PipelineActivityAction.NotesUpdated,
      ],
    },
    {
      // SE-5628 added ASSIGNED / UNASSIGNED to the feed. They get their own
      // chip rather than joining an existing bundle: assignment is a distinct
      // question a manager asks ("who is this on?"), and the nearest bundle —
      // `membership`, which is about the card entering or leaving the board —
      // means something else entirely. Both members belong together, for the
      // same reason `TAG_APPLIED`/`TAG_REMOVED` share one chip: the pair reads
      // as one kind of event whose direction the row already shows.
      key: "assignment",
      label: "Assignment",
      values: [
        PipelineActivityAction.Assigned,
        PipelineActivityAction.Unassigned,
      ],
    },
    {
      key: "lifecycle",
      label: "Closed / reopened",
      values: [PipelineActivityAction.Closed, PipelineActivityAction.Reopened],
    },
  ];

/**
 * Actor chips.
 *
 * `AUTOMATED` deliberately bundles `SYSTEM` with `AUTOMATION_RULE` so it
 * matches the automation marker rendered on each row exactly — see
 * `isAutomatedActor`. Splitting them would let a user select "Automation" and
 * still see marked rows they had just filtered out.
 */
export const ACTOR_FILTER_CHIPS: readonly PipelineActivityFilterChip<PipelineActorType>[] =
  [
    {
      key: "people",
      label: "People",
      values: [PipelineActorType.User],
    },
    {
      key: "automated",
      label: "Automated",
      values: [PipelineActorType.AutomationRule, PipelineActorType.System],
    },
  ];

/**
 * Expand a set of selected chip keys into the flat enum list the query takes.
 *
 * Returns a de-duplicated list in catalogue order so the variables are stable
 * regardless of the order the user pressed the chips in — otherwise Apollo
 * would treat `[MOVED, ADDED]` and `[ADDED, MOVED]` as different cache entries
 * and refetch on a no-op reorder.
 * @param chips - The chip catalogue to expand against.
 * @param selectedKeys - Keys of the chips currently selected.
 * @returns The union of the selected chips' values, in catalogue order.
 */
export const expandFilterChips = <TValue>(
  chips: readonly PipelineActivityFilterChip<TValue>[],
  selectedKeys: ReadonlySet<string>
): readonly TValue[] => [
  ...new Set(
    chips
      .filter(chip => selectedKeys.has(chip.key))
      .flatMap(chip => [...chip.values])
  ),
];

/**
 * Toggle one chip key in a selection set, returning a NEW set.
 *
 * Immutable by contract: the result is handed to `useState`, and mutating the
 * existing set in place would keep the same identity and skip the re-render.
 * @param selectedKeys - The current selection.
 * @param key - The chip that was pressed.
 * @returns The next selection.
 */
export const toggleFilterChip = (
  selectedKeys: ReadonlySet<string>,
  key: string
): ReadonlySet<string> =>
  new Set(
    selectedKeys.has(key)
      ? [...selectedKeys].filter(candidate => candidate !== key)
      : [...selectedKeys, key]
  );
